import React, { useState, useEffect } from 'react';
import { getScenarios, createScenario, updateScenario, deleteScenario, uploadGradingSheet, uploadTranscript } from '../services/api';

const styles = {
  heading: { fontSize: '24px', fontWeight: '700', color: '#1a202c', marginBottom: '24px' },
  toolbar: { display: 'flex', gap: '12px', marginBottom: '24px' },
  addBtn: {
    padding: '10px 20px', background: '#9C3D97', color: '#fff', border: 'none',
    borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
  },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  th: { padding: '12px 16px', textAlign: 'left', background: '#f7fafc', fontSize: '12px', fontWeight: '600', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px' },
  td: { padding: '12px 16px', borderTop: '1px solid #e2e8f0', fontSize: '14px', color: '#4a5568' },
  // Narrow variants for the status-style columns (Suspense Date / Grading
  // Sheet / Transcript) so the Case Name column keeps room on small laptop
  // screens - graders need to see which case they're uploading files for.
  thNarrow: { padding: '12px 10px' },
  tdNarrow: { padding: '12px 10px' },
  badge: { padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' },
  badgeExpired: { background: '#fed7d7', color: '#c53030' },
  badgeUploaded: { background: '#c6f6d5', color: '#276749' },
  badgeMissing: { background: '#fed7d7', color: '#c53030' },
  actionBtn: {
    padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '4px',
    background: '#fff', fontSize: '13px', cursor: 'pointer', marginRight: '8px',
  },
  modal: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000,
  },
  modalCard: { background: '#fff', borderRadius: '12px', padding: '32px', width: '520px', maxHeight: '80vh', overflow: 'auto' },
  modalTitle: { fontSize: '20px', fontWeight: '700', marginBottom: '20px' },
  label: { display: 'block', fontSize: '14px', fontWeight: '600', color: '#4a5568', marginBottom: '6px', marginTop: '16px' },
  required: { color: '#e53e3e', marginLeft: '2px' },
  input: { width: '100%', padding: '10px 14px', border: '2px solid #e2e8f0', borderRadius: '6px', fontSize: '14px', outline: 'none' },
  fileUploadBox: {
    border: '2px dashed #cbd5e0', borderRadius: '8px', padding: '20px', textAlign: 'center',
    cursor: 'pointer', background: '#f7fafc', marginBottom: '8px', transition: 'all 0.2s',
  },
  fileUploadBoxHasFile: {
    border: '2px solid #48bb78', background: '#f0fff4',
  },
  fileLabel: { fontSize: '14px', color: '#4a5568' },
  fileLabelSub: { fontSize: '12px', color: '#a0aec0', marginTop: '4px' },
  fileName: { fontSize: '14px', color: '#276749', fontWeight: '600' },
  modalActions: { display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' },
  cancelBtn: { padding: '10px 20px', background: '#edf2f7', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' },
  saveBtn: { padding: '10px 20px', background: '#9C3D97', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  saveBtnDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  success: { background: '#c6f6d5', color: '#276749', padding: '12px', borderRadius: '6px', marginBottom: '16px', fontSize: '14px' },
  error: { background: '#fed7d7', color: '#c53030', padding: '12px', borderRadius: '6px', marginBottom: '16px', fontSize: '14px' },
  divider: { margin: '24px 0', borderColor: '#e2e8f0', borderStyle: 'solid', borderWidth: '1px 0 0 0' },
};

function formatDateAZ(dateStr) {
  if (!dateStr) return null;
  // For date-only strings (YYYY-MM-DD), parse as UTC noon so that converting
  // to AZ (UTC-7) always lands on the same calendar day, regardless of what
  // timezone the browser is in. Parsing without the 'Z' suffix would use the
  // browser's local time, which drifts for users far east of AZ (e.g., VAs
  // in the Philippines at UTC+8 would see the previous calendar day).
  const adjusted = (typeof dateStr === 'string' && dateStr.length === 10)
    ? new Date(dateStr + 'T12:00:00Z')
    : new Date(dateStr);
  if (isNaN(adjusted)) return dateStr;
  return adjusted.toLocaleDateString('en-US', { timeZone: 'America/Phoenix', month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTimeAZ(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleString('en-US', { timeZone: 'America/Phoenix', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }) + ' AZ';
}

export default function ScenariosPage() {
  const [scenarios, setScenarios] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showUpload, setShowUpload] = useState(null);
  const [showRename, setShowRename] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const [newName, setNewName] = useState('');
  const [newSuspenseDate, setNewSuspenseDate] = useState('');
  const [gradingFile, setGradingFile] = useState(null);
  const [transcriptFile, setTranscriptFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [listLoading, setListLoading] = useState(true);

  useEffect(() => { loadScenarios(); }, []);

  async function loadScenarios() {
    setListLoading(true);
    try {
      const data = await getScenarios();
      setScenarios(data.scenarios || []);
    } catch { setError('Failed to load cases.'); }
    finally { setListLoading(false); }
  }

  async function handleCreate() {
    if (!newName.trim()) return;
    const result = await createScenario(newName.trim(), newSuspenseDate || '');
    if (result.error) { setError(result.error); }
    else { setMessage(`Case "${newName}" created.`); setShowCreate(false); setNewName(''); setNewSuspenseDate(''); loadScenarios(); }
  }

  async function handleUploadBoth() {
    if (!gradingFile || !transcriptFile) {
      setError('Both grading sheet and transcript PDF are required.');
      return;
    }
    setUploading(true);
    setError('');

    try {
      // Upload grading sheet
      const sheetResult = await uploadGradingSheet(showUpload, gradingFile);
      if (sheetResult.error) {
        const validationErrors = sheetResult.validation_errors || [];
        const validationWarnings = sheetResult.validation_warnings || [];
        let msg = sheetResult.error;
        if (validationErrors.length > 0) {
          msg += '\n\n' + validationErrors.map((e, i) => `${i + 1}. ${e}`).join('\n');
        }
        if (validationWarnings.length > 0) {
          msg += '\n\nWarnings:\n' + validationWarnings.map(w => `- ${w}`).join('\n');
        }
        setError(msg);
        setUploading(false);
        return;
      }

      // Upload transcript PDF
      const transcriptResult = await uploadTranscript(showUpload, transcriptFile);
      if (transcriptResult.error) {
        setError(`Transcript error: ${transcriptResult.error}`);
        setUploading(false);
        return;
      }

      let successMsg = `Files uploaded. ${sheetResult.extraction_summary?.total_chart_detail_items || 0} grading items extracted. ${transcriptResult.character_count || 0} transcript characters.`;
      const uploadWarnings = sheetResult.validation_warnings || [];
      if (uploadWarnings.length > 0) {
        successMsg += '\n\nWarnings:\n' + uploadWarnings.map(w => `- ${w}`).join('\n');
      }
      setMessage(successMsg);
      setShowUpload(null);
      setGradingFile(null);
      setTranscriptFile(null);
      loadScenarios();
    } catch (e) {
      setError(`Upload failed: ${e.message || 'Network error. Please try again.'}`);
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(id, name) {
    if (!window.confirm(`Delete case "${name}" and all associated files? This cannot be undone.`)) return;
    setError('');
    const result = await deleteScenario(id);
    if (result.error) { setError(result.error); }
    else { setMessage(`Case "${name}" deleted.`); loadScenarios(); }
  }

  function openUploadModal(id) {
    setShowUpload(id);
    setGradingFile(null);
    setTranscriptFile(null);
    setError('');
  }

  function openRenameModal(s) {
    setShowRename(s);
    setRenameValue(s.name || '');
    setError('');
    setMessage('');
  }

  async function handleRenameSave() {
    if (!showRename) return;
    const trimmed = (renameValue || '').trim();
    if (!trimmed) {
      setError('Case name cannot be empty.');
      return;
    }
    if (trimmed === (showRename.name || '').trim()) {
      setShowRename(null);
      return;
    }
    setRenameSaving(true);
    setError('');
    try {
      const result = await updateScenario(showRename.id, { name: trimmed });
      if (result.error) {
        setError(result.error);
        setRenameSaving(false);
        return;
      }
      setMessage(`Case renamed to "${trimmed}".`);
      setShowRename(null);
      setRenameValue('');
      loadScenarios();
    } catch (e) {
      setError(`Rename failed: ${e.message || 'Network error.'}`);
    } finally {
      setRenameSaving(false);
    }
  }

  return (
    <div>
      <h1 style={styles.heading}>Case Management</h1>
      {message && <div style={{ ...styles.success, whiteSpace: 'pre-wrap' }}>{message}</div>}
      {error && <div style={{ ...styles.error, whiteSpace: 'pre-wrap' }}>{error}</div>}

      <div style={styles.toolbar}>
        <button style={styles.addBtn} onClick={() => setShowCreate(true)}>+ New Case</button>
      </div>

      {/* tableLayout fixed: without it the browser sizes columns by content
          and the width hints below are ignored - long case names were
          auto-expanding the Case Name column to half the table. */}
      <table style={{ ...styles.table, tableLayout: 'fixed' }}>
        <thead>
          <tr>
            <th style={{ ...styles.th, width: '220px' }}>Case Name</th>
            <th style={{ ...styles.th, ...styles.thNarrow, width: '156px' }}>Suspense Date</th>
            <th style={{ ...styles.th, ...styles.thNarrow, width: '90px' }}>Grading Sheet</th>
            <th style={{ ...styles.th, ...styles.thNarrow, width: '90px' }}>Transcript</th>
            <th style={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {scenarios.map((s) => (
            <tr key={s.id}>
              <td style={{ ...styles.td, overflowWrap: 'break-word' }}>{s.name}</td>
              <td style={{ ...styles.td, ...styles.tdNarrow }}>
                <span style={{ fontWeight: '600', color: s.expired ? '#c53030' : '#4a5568', whiteSpace: 'nowrap' }}>
                  {s.suspense_date ? formatDateAZ(s.suspense_date) : <span style={{ fontWeight: '400', color: '#a0aec0' }}>No deadline</span>}
                </span>
                {/* The space before the badge is a deliberate wrap point: the
                    two spans were adjacent with no whitespace, so the badge
                    could not wrap and was clipped at the column edge. */}
                {s.expired && <>{' '}<span style={{ ...styles.badge, ...styles.badgeExpired, display: 'inline-block', marginTop: '4px' }}>Expired</span></>}
              </td>
              <td style={{ ...styles.td, ...styles.tdNarrow }}>
                <span style={{ ...styles.badge, ...(s.has_grading_sheet ? styles.badgeUploaded : styles.badgeMissing) }}>
                  {s.has_grading_sheet ? 'Uploaded' : 'Missing'}
                </span>
              </td>
              <td style={{ ...styles.td, ...styles.tdNarrow }}>
                <span style={{ ...styles.badge, ...(s.has_transcript ? styles.badgeUploaded : styles.badgeMissing) }}>
                  {s.has_transcript ? 'Uploaded' : 'Missing'}
                </span>
              </td>
              <td style={styles.td}>
                <button
                  style={{
                    ...styles.actionBtn,
                    ...(s.upload_locked ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                  }}
                  onClick={() => !s.upload_locked && openUploadModal(s.id)}
                  disabled={!!s.upload_locked}
                  title={s.upload_locked ? ('Upload locked: ' + (s.upload_locked_reason || 'Case is in an active batch.')) : 'Upload grading sheet and/or transcript'}
                >
                  Upload Files{s.upload_locked ? ' (locked)' : ''}
                </button>
                <button
                  style={{
                    ...styles.actionBtn,
                    ...(s.rename_locked ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                  }}
                  onClick={() => !s.rename_locked && openRenameModal(s)}
                  disabled={!!s.rename_locked}
                  title={s.rename_locked ? ('Name locked: ' + (s.rename_locked_reason || 'Case is in an active batch.')) : 'Rename this case'}
                >
                  Rename{s.rename_locked ? ' (locked)' : ''}
                </button>
                <button style={{ ...styles.actionBtn, color: '#c53030', borderColor: '#feb2b2' }} onClick={() => handleDelete(s.id, s.name)}>
                  Delete
                </button>
              </td>
            </tr>
          ))}
          {listLoading && scenarios.length === 0 && (
            <tr><td style={{ ...styles.td, textAlign: 'center', color: '#718096' }} colSpan={5}>Loading cases...</td></tr>
          )}
          {!listLoading && scenarios.length === 0 && (
            <tr><td style={{ ...styles.td, textAlign: 'center', color: '#a0aec0' }} colSpan={5}>No cases yet. Click "+ New Case" to get started.</td></tr>
          )}
        </tbody>
      </table>

      {/* Create Modal */}
      {showCreate && (
        <div style={styles.modal}>
          <div style={styles.modalCard}>
            <h2 style={styles.modalTitle}>Create New Case</h2>
            <label style={styles.label}>Case Name<span style={styles.required}>*</span></label>
            <input style={styles.input} value={newName} onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g., 47 Benjamin LBP" autoFocus />
            <label style={styles.label}>Suspense Date <span style={{ fontWeight: '400', color: '#a0aec0' }}>(optional)</span></label>
            <input style={styles.input} type="date" value={newSuspenseDate}
              onChange={(e) => setNewSuspenseDate(e.target.value)} />
            <p style={{ fontSize: '12px', color: '#718096', marginTop: '4px' }}>
              {newSuspenseDate
                ? 'VAs will not be able to submit evaluations after this date.'
                : 'No deadline — VAs can submit at any time unless a date is set.'}
            </p>
            <div style={styles.modalActions}>
              <button style={styles.cancelBtn} onClick={() => { setShowCreate(false); setNewSuspenseDate(''); }}>Cancel</button>
              <button style={{ ...styles.saveBtn, ...(!newName.trim() ? styles.saveBtnDisabled : {}) }}
                onClick={handleCreate} disabled={!newName.trim()}>Create</button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Modal */}
      {showRename && (
        <div style={styles.modal}>
          <div style={styles.modalCard}>
            <h2 style={styles.modalTitle}>Rename Case</h2>
            <div style={{
              background: '#fffaf0', border: '2px solid #ed8936', borderRadius: '6px',
              padding: '14px', marginBottom: '18px', fontSize: '13px', color: '#7b341e', lineHeight: '1.5',
            }}>
              <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '6px' }}>
                Important: case names are a permanent part of the audit trail.
              </div>
              <div>
                Once a SOAP note has been graded or archived under a case name, that
                name stays with the submission forever. Renaming the case here
                changes the name only for FUTURE batches.
              </div>
              <div style={{ marginTop: '8px' }}>
                Renaming is automatically blocked while the case is in an active
                batch or has submissions still being graded. The grading tool relies
                on the case name being consistent for the whole lifecycle of a
                batch, so there is no way to undo a mid-batch rename.
              </div>
              <div style={{ marginTop: '8px', fontWeight: '600' }}>
                Rename only between batches, when the case is idle.
              </div>
            </div>

            <label style={styles.label}>Current Name</label>
            <input style={{ ...styles.input, background: '#f7fafc', color: '#a0aec0' }} value={showRename.name || ''} disabled />

            <label style={styles.label}>New Name<span style={styles.required}>*</span></label>
            <input
              style={styles.input}
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              placeholder="e.g., Patient 1 Day 1"
              autoFocus
            />
            <p style={{ fontSize: '12px', color: '#718096', marginTop: '6px' }}>
              Must be unique across all cases. Not case-sensitive.
            </p>

            <div style={styles.modalActions}>
              <button
                style={styles.cancelBtn}
                onClick={() => { setShowRename(null); setRenameValue(''); setError(''); }}
                disabled={renameSaving}
              >
                Cancel
              </button>
              <button
                style={{ ...styles.saveBtn, ...((!renameValue.trim() || renameSaving) ? styles.saveBtnDisabled : {}) }}
                onClick={handleRenameSave}
                disabled={!renameValue.trim() || renameSaving}
              >
                {renameSaving ? 'Saving...' : 'Rename Case'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUpload && (
        <div style={styles.modal}>
          <div style={styles.modalCard}>
            <h2 style={styles.modalTitle}>Upload Case Files</h2>
            {/* Show which case these files are for - on small laptop screens
                the table row may be scrolled out of view behind the modal. */}
            <div style={{ background: '#faf5ff', border: '1px solid #d6bcfa', borderRadius: '6px', padding: '10px 14px', marginBottom: '12px', fontSize: '14px', color: '#553c9a' }}>
              Uploading for: <strong>{(scenarios.find((s) => s.id === showUpload) || {}).name || ''}</strong>
            </div>
            <p style={{ fontSize: '14px', color: '#718096', marginBottom: '16px' }}>
              Both files are required for grading to work.
            </p>

            <label style={styles.label}>
              Grading Sheet (Excel .xlsx)<span style={styles.required}>*</span>
            </label>
            <div
              style={{ ...styles.fileUploadBox, ...(gradingFile ? styles.fileUploadBoxHasFile : {}) }}
              onClick={() => document.getElementById('grading-file-input').click()}
            >
              {gradingFile ? (
                <div style={styles.fileName}>{gradingFile.name}</div>
              ) : (
                <>
                  <div style={styles.fileLabel}>Click to select .xlsx file</div>
                  <div style={styles.fileLabelSub}>Excel grading sheet with scoring criteria</div>
                </>
              )}
            </div>
            <input id="grading-file-input" type="file" accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={(e) => { if (e.target.files[0]) setGradingFile(e.target.files[0]); e.target.value = ''; }}
            />

            <div style={styles.divider} />

            <label style={styles.label}>
              Video Transcript (PDF)<span style={styles.required}>*</span>
            </label>
            <div
              style={{ ...styles.fileUploadBox, ...(transcriptFile ? styles.fileUploadBoxHasFile : {}) }}
              onClick={() => document.getElementById('transcript-file-input').click()}
            >
              {transcriptFile ? (
                <div style={styles.fileName}>{transcriptFile.name}</div>
              ) : (
                <>
                  <div style={styles.fileLabel}>Click to select PDF file</div>
                  <div style={styles.fileLabelSub}>PDF transcript of the patient encounter video</div>
                </>
              )}
            </div>
            <input id="transcript-file-input" type="file" accept=".pdf,application/pdf"
              style={{ display: 'none' }}
              onChange={(e) => { if (e.target.files[0]) setTranscriptFile(e.target.files[0]); e.target.value = ''; }}
            />

            <div style={styles.modalActions}>
              <button style={styles.cancelBtn} onClick={() => { setShowUpload(null); setGradingFile(null); setTranscriptFile(null); setUploading(false); }}>Cancel</button>
              <button
                style={{ ...styles.saveBtn, ...(!gradingFile || !transcriptFile || uploading ? styles.saveBtnDisabled : {}) }}
                onClick={handleUploadBoth}
                disabled={!gradingFile || !transcriptFile || uploading}
              >
                {uploading ? 'Uploading...' : 'Upload Both Files'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
